import { z } from 'zod';

import { CustomQuestionAnswerBookingResponseSchema, PurchaserWithCustomQuestionsSchema, BaseParticipantSchema } from '../..';
import { DateStringSchema, EmailStringSchema } from '../../common';
import { makeEndpoint } from '../common';

export enum BookingInquiryStatus {
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  UNAVAILABLE = 'unavailable',
  EXPIRED = 'expired',
}

export enum DateType {
  HOTEL = 'hotel',
  STANDARD = 'standard',
}

export enum InquiryItemType {
  ITEM = 'item',
  TAX = 'tax',
}

export const BookingInquiryItemSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  type: z.nativeEnum(InquiryItemType),
  amount: z.number(),
});

export const BookingInquirySchema = z.object({
  id: z.string().uuid(),
  status: z.nativeEnum(BookingInquiryStatus),
  experienceId: z.string().uuid(),
  experience: z.object({
    id: z.string(),
    title: z.string(),
  }),
  customerId: z.string().uuid(),
  customer: z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string(),
    phoneNumber: z.string().nullable(),
  }),
  purchaserId: z.string().uuid(),
  purchaser: PurchaserWithCustomQuestionsSchema.extend({
    emailAddress: EmailStringSchema,
    customQuestionResponses: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
  }).omit({
    id: true,
  }),
  participants: z.array(
    BaseParticipantSchema.extend({
      customQuestionResponses: z.array(CustomQuestionAnswerBookingResponseSchema).optional(),
    }).omit({
      priceTierName: true,
    }),
  ),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
  dateType: z.nativeEnum(DateType),
  message: z.string().nullable(),
  inquiryItems: z.array(BookingInquiryItemSchema).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const BaseBookingInquiryRequestSchema = z.object({
  customer: z.object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string(),
    phoneNumber: z.string().nullable(),
  }),
  purchaser: PurchaserWithCustomQuestionsSchema.pick({
    firstName: true,
    lastName: true,
    emailAddress: true,
    phoneNumber: true,
    customQuestionResponses: true,
  }).required({ emailAddress: true }),
  inquiryItems: z
    .array(
      z.object({
        label: z.string(),
        type: z.nativeEnum(InquiryItemType),
        amount: z.number(),
      }),
    )
    .optional(),
  startDate: DateStringSchema.nullable(),
  endDate: DateStringSchema.nullable(),
  dateType: z.nativeEnum(DateType),
  message: z.string().nullable().optional(),
});

const UpdateBookingInquiryRequestSchema = BaseBookingInquiryRequestSchema.extend({
  status: z.nativeEnum(BookingInquiryStatus).optional(),
});

export const CreateBookingInquiryRequestSchema = BaseBookingInquiryRequestSchema.strict();

export type CreateBookingInquiryRequest = z.infer<typeof CreateBookingInquiryRequestSchema>;

export const PatchBookingInquiryRequestSchema = UpdateBookingInquiryRequestSchema.partial().strict();

export type PatchBookingInquiryRequest = z.infer<typeof PatchBookingInquiryRequestSchema>;

export const GetBookingInquiriesQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  experienceId: z.string().uuid().optional(),
  status: z.nativeEnum(BookingInquiryStatus).optional(),
  searchQuery: z.string().optional(),
  withDeleted: z.boolean().optional(),
});

export type GetBookingInquiriesQuery = z.infer<typeof GetBookingInquiriesQuerySchema>;

export const CreateBookingInquiryEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, experienceId }: { brandId: string; experienceId: string }) =>
    `v1/brands/${brandId}/experiences/${experienceId}/inquiry`,
  requestSchema: CreateBookingInquiryRequestSchema,
  responseSchema: BookingInquirySchema,
});

export const PatchBookingInquiryEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, experienceId, inquiryId }: { brandId: string; experienceId: string; inquiryId: string }) =>
    `v1/brands/${brandId}/experiences/${experienceId}/inquiry/${inquiryId}`,
  requestSchema: PatchBookingInquiryRequestSchema,
  responseSchema: BookingInquirySchema,
});

export const DeleteBookingInquiryEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, experienceId, inquiryId }: { brandId: string; experienceId: string; inquiryId: string }) =>
    `v1/brands/${brandId}/experiences/${experienceId}/inquiry/${inquiryId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const GetBookingInquiriesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/inquiries`,
  requestSchema: GetBookingInquiriesQuerySchema,
  responseSchema: z.object({
    items: z.array(BookingInquirySchema),
    meta: z.object({
      totalItems: z.number(),
      itemCount: z.number(),
      itemsPerPage: z.number(),
      totalPages: z.number(),
      currentPage: z.number(),
    }),
  }),
});

export const GetBookingInquiryEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, inquiryId }: { brandId: string; inquiryId: string }) => `v1/brands/${brandId}/inquiries/${inquiryId}`,
  requestSchema: z.void(),
  responseSchema: BookingInquirySchema,
});
